import { IWPImage } from "@/src/types";
import React from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

import S from "./MainHero.styled";
import { MainInner } from "@/styles/global";

import { nlToBr } from "@/src/helpers";
import { Wysiwyg } from "@/styles/typography";

export interface Props {
  title1: string;
  title2: string;
  subtitle: string;
  text: string;
  backgroundImage?: IWPImage;
  backgroundVideo?: {
    mediaItemUrl: string;
    mimeType: string;
  };
  selectMedia: string;
  limitText: string;
  embedVimeo: string;
}

const MainHero: React.FC<Props> = ({
  title1,
  title2,
  subtitle,
  text,
  backgroundImage,
  backgroundVideo,
  selectMedia,
  limitText,
  embedVimeo,
}) => {
  const [isTextLimited, setIsTextLimited] = React.useState(false);

  const backgroundImageData = backgroundImage?.localFile?.childImageSharp
    ?.gatsbyImageData
    ? getImage(backgroundImage.localFile.childImageSharp.gatsbyImageData)
    : null;

  React.useEffect(() => {
    if (limitText === "yes") {
      setIsTextLimited(true);
    }
  }, [limitText]);

  const [formattedVimeo, setFormattedVimeo] = React.useState("");

  React.useEffect(() => {
    if (embedVimeo) {
      if (!embedVimeo.includes("autoplay=1")) {
        const formatted = embedVimeo.replace(
          "badge=0",
          "muted=1&badge=0&autoplay=1"
        );
        setFormattedVimeo(formatted);
      } else {
        const formatted = embedVimeo.replace("badge=0", "muted=1&badge=0");
        setFormattedVimeo(formatted);
      }
    }
  }, [embedVimeo]);

  const [isHome, setIsHome] = React.useState(false);

  React.useEffect(() => {
    // check if on home page
    if (window.location.pathname === "/") {
      setIsHome(true);
    }
  }, []);

  return (
    <S.Container isHome={isHome}>
      {backgroundImageData && selectMedia === "image" && (
        <S.ImageWrapper>
          <S.Image
            image={backgroundImageData}
            alt={backgroundImage?.altText || ""}
          />
        </S.ImageWrapper>
      )}
      {backgroundVideo && selectMedia === "video" && (
        <S.BackgroundVideo>
          <video autoPlay loop muted playsInline>
            <source
              src={backgroundVideo.mediaItemUrl}
              type={backgroundVideo.mimeType}
            />
          </video>
        </S.BackgroundVideo>
      )}
      {embedVimeo && selectMedia === "Vimeo" && (
        <S.VimeoHero isHome={isHome}>
          <iframe
            src={`https://player.vimeo.com/video/${embedVimeo}?background=1&autoplay=1&loop=1&title=0&byline=0&portrait=0&muted=1&autopause=0&controls=0`}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
          ></iframe>
        </S.VimeoHero>
      )}
      <MainInner>
        <S.TextContainer>
          <S.TitleWrapper>
            <S.Title1>{title1 && title1}</S.Title1>
            <S.Title2>{title2 && title2}</S.Title2>
          </S.TitleWrapper>
          <S.TextWrapper>
            <S.Subtitle>{subtitle && subtitle}</S.Subtitle>
            <S.Text isTextLimited={isTextLimited}>
              {text && nlToBr(text)}
            </S.Text>
            {limitText === "yes" && (
              <>
                {isTextLimited ? (
                  <S.ShowMore
                    isTextLimited={isTextLimited}
                    onClick={() => setIsTextLimited(false)}
                  >
                    More +
                  </S.ShowMore>
                ) : (
                  <S.ShowMore
                    isTextLimited={isTextLimited}
                    onClick={() => setIsTextLimited(true)}
                  >
                    Less -
                  </S.ShowMore>
                )}
              </>
            )}
          </S.TextWrapper>
        </S.TextContainer>
      </MainInner>
    </S.Container>
  );
};

export default MainHero;
